import React, { useState } from 'react';
import { TextField, Checkbox, FormControlLabel, Button, Box, Typography, InputLabel, Select, MenuItem, FormControl } from '@mui/material';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { setProfileData, deleteProfile, cancelDeleteRequest } from "../../profile/profileSlice";
import { toast } from "react-toastify";
import api from "../../api/api";
import { CountriesList } from "../../common/countries-list";
import pl from 'react-phone-number-input/locale/pl.json'
import 'react-phone-number-input/style.css'
import PhoneInput from 'react-phone-number-input'
import "./Profile.css";
import YellowButton from "../../common/buttons/YellowButton";
import {trackSignUp} from "../../../analytics/events";

interface ProfileProps { formTitle?: string }

const Profile: React.FC<ProfileProps> = ({ formTitle }) => {
    const { t } = useTranslation();
    const [isUpdating, setIsUpdating] = useState(false);
    const profileData = useSelector((state: RootState) => state.profile);
    const dispatch = useDispatch();
    let formTitleToPut = formTitle ?? t("Edit Profile");

    const [form, setForm] = useState({
        name: profileData.name,
        email: profileData.email,
        phone: profileData.phone,
        invoiceToCompany: profileData.invoiceToCompany,
        companyName: profileData.companyName,
        companyEmail: profileData.companyEmail,
        companyNIP: profileData.companyNIP,
        companyCountry: profileData.companyCountry,
        companyAddress: profileData.companyAddress,
        agreement: profileData.agreement,
        marketingEmails: profileData.marketingEmails
    });

    const [errors, setErrors] = useState({
        name: "",
        agreement: "",
        companyName: "",
        companyNIP: ""
    });

    const handleChange = (event: any) => {
        const { name, value, checked, type } = event.target;
        setForm((prevForm) => ({ ...prevForm, [name]: type === 'checkbox' ? checked : value }));
        // Clear error message when user corrects input
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    };

    const setNewPhone = (phoneNumber?: string | null) => {
        const theNumber: string = phoneNumber ? phoneNumber : "";
        setForm((prevForm) => ({ ...prevForm, phone: theNumber }));
    };

    const validateForm = () => {
        let isValid = true;
        let newErrors: any = {};

        if (!form.name) {
            newErrors.name = t("Name is required");
            isValid = false;
        }

        if (!form.agreement) {
            newErrors.agreement = t("Agreement to terms and conditions is required");
            isValid = false;
        }

        if (form.invoiceToCompany) {
            if (!form.companyName) {
                newErrors.companyName = t("Company Name is required");
                isValid = false;
            }
            if (!form.companyNIP) {
                newErrors.companyNIP = t("Company NIP (VAT) is required");
                isValid = false;
            }
        }

        setErrors(newErrors);
        return isValid;
    };
    const deleteProfileRequest = async (event: any) => {
        try {
            await api().delete('users/profile');
            dispatch(deleteProfile());
            toast.success(t("Profile delete request has been submitted successfully"));
        } catch {
            toast.error(t("Failed to submit delete request. Please try again."));
        }
    };

    const cancelProfileDeleteRequest = async (event: any) => {
        try {
            await api().patch('users/profile-cancel-delete-request');
            dispatch(cancelDeleteRequest());
            toast.success(t("Profile delete request has been cancelled successfully"));
        } catch {
            toast.error(t("Failed to cancel delete request. Please try again."));
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!validateForm()) return;

        setIsUpdating(true);
        const isFirstCompletion = !profileData.agreement && !!form.agreement;
        try {
            await api().patch('users/profile', {
                name: form.name,
                phone: form.phone ?? "",
                companyName: form.companyName ?? "",
                companyNIP: form.companyNIP ?? "",
                companyAddress: form.companyAddress ?? "",
                companyCountry: form.companyCountry ?? "PL",
                companyEmail: form.companyEmail ?? "",
                agreement: !!form.agreement,
                marketingEmails: !!form.marketingEmails,
                invoiceToCompany: !!form.invoiceToCompany
            });
            dispatch(setProfileData({
                _id: profileData._id,
                externalId: profileData.externalId,
                name: form.name,
                email: profileData.email,
                phone: form.phone ?? "",
                role: profileData.role,
                companyNIP: form.companyNIP ?? "",
                companyCountry: '',
                companyAddress: '',
                companyName: form.companyName ?? "",
                companyEmail: form.companyEmail ?? "",
                agreement: form.agreement,
                invoiceToCompany: form.invoiceToCompany,
                marketingEmails: form.marketingEmails,
                deleteRequestSubmitted: profileData.deleteRequestSubmitted
            }));
            if (isFirstCompletion) {
                trackSignUp();
            }
            toast.success(t("Profile has been updated successfully"));
        } catch {
            toast.error(t("Failed to update profile. Please try again."));
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 600, margin: 'auto', padding: 2, paddingTop: "50px" }}>
            <Typography variant="h4" component="h1">
                {isUpdating ? t("Profile is updating") : formTitleToPut}
            </Typography>
            <form onSubmit={handleSubmit}>
                <TextField
                    name="name"
                    label={t("Name")}
                    fullWidth
                    margin="normal"
                    value={form.name}
                    onChange={handleChange}
                    required
                    error={!!errors.name}
                    helperText={errors.name}
                />
                <TextField
                    name="email"
                    label={t("Email")}
                    fullWidth
                    margin="normal"
                    value={form.email}
                    disabled
                />
                <PhoneInput
                    className={"profile-phone"}
                    placeholder={t("Phone Number (optional)")}
                    value={form.phone}
                    labels={pl}
                    defaultCountry={"PL"}
                    onChange={setNewPhone}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            name="invoiceToCompany"
                            checked={form.invoiceToCompany}
                            onChange={handleChange}
                        />
                    }
                    label={t("Invoice to company")}
                />
                {form.invoiceToCompany && (
                    <>
                        <TextField
                            name="companyName"
                            label={t("Company Name")}
                            fullWidth
                            margin="normal"
                            value={form.companyName}
                            onChange={handleChange}
                            required={form.invoiceToCompany}
                            error={!!errors.companyName}
                            helperText={errors.companyName}
                        />
                        <TextField
                            name="companyNIP"
                            label={t("Company NIP (VAT)")}
                            fullWidth
                            margin="normal"
                            value={form.companyNIP}
                            onChange={handleChange}
                            required={form.invoiceToCompany}
                            error={!!errors.companyNIP}
                            helperText={errors.companyNIP}
                        />
                        <TextField
                            name="companyEmail"
                            label={t("Company email (optional)")}
                            fullWidth
                            margin="normal"
                            type="email"
                            value={form.companyEmail}
                            onChange={handleChange}
                            required={false}
                        />
                        <FormControl sx={{ minWidth: 180, marginTop: "15px", marginBottom: "10px" }}>
                            <InputLabel shrink>{t("Company country")}</InputLabel>
                            <Select
                                name="companyCountry"
                                value={form.companyCountry}
                                onChange={handleChange}
                                label={t("Company country")}
                            >
                                {Object.entries(CountriesList).map(([code, name]) => (
                                    <MenuItem key={code} value={code}>{name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            name="companyAddress"
                            label={t("Company address (optional)")}
                            fullWidth
                            margin="normal"
                            type="text"
                            value={form.companyAddress}
                            onChange={handleChange}
                            required={false}
                        />
                    </>
                )}
                {profileData.deleteRequestSubmitted ?
                <div className={"profile-delete"} onClick={cancelProfileDeleteRequest}>{t("Cancel delete profile request")}</div>
                : <div className={"profile-delete"} onClick={deleteProfileRequest}>{t("Delete profile")}</div>
                }
                <br />
                <FormControlLabel
                    control={
                        <Checkbox
                            name="agreement"
                            checked={form.agreement}
                            onChange={handleChange}
                        />
                    }
                    label={t("I agree to terms and conditions and privacy policy")}
                />
                {errors.agreement && <Typography color="error">{errors.agreement}</Typography>}
                <FormControlLabel
                    control={
                        <Checkbox
                            name="marketingEmails"
                            checked={form.marketingEmails}
                            onChange={handleChange}
                        />
                    }
                    label={t("Send me feature updates and marketing emails")}
                />
                <Box sx={{ marginTop: 2 }}>
                    <YellowButton
                        type="submit"
                    >
                        {t("Save")}
                    </YellowButton>
                </Box>
            </form>
        </Box>
    );
};

export default Profile;
